import type { ActivityDetail, ActivitySummary } from '@painpal/contracts';
import { thresholdSport } from '@painpal/core';
import {
  formatDuration, formatKm, formatKmValue, formatMonthDay, formatPaceKm, formatTimeOfDay, formatWeekdayMonthDay, formatWhole, orDash,
} from '../../lib/format';
import { sourceSportLabel } from './runDetailModel';

export interface Tile { key: string; label: string; value: string; note: string; muted?: boolean }
export interface LoadFormula { math: string; threshold: string }

type ThresholdUsed = NonNullable<ActivityDetail['thresholdUsed']>;

function thresholdText(a: Pick<ActivityDetail, 'sport'>, used: ThresholdUsed): string {
  const since = `SINCE ${formatMonthDay(used.effectiveFrom)}`;
  switch (used.kind) {
    case 'ftp':
      return `FTP ${used.value} W (${since})`;
    case 'threshold_pace':
      return `THRESHOLD PACE ${formatPaceKm(used.value)} (${since})`;
    case 'max_hr':
      return `MAX HR ${used.value} BPM (${since})`;
    case 'lthr': {
      const group = thresholdSport(a.sport);
      if (group === 'other') return `THRESHOLD HR ${used.value} BPM (OTHER SPORTS · ${since})`;
      return `${group === 'run' ? 'RUN THRESHOLD HR' : 'LTHR'} ${used.value} BPM (${since})`;
    }
  }
}

export function loadFormula(activity: ActivityDetail): LoadFormula | null {
  const { load, intensity, thresholdUsed } = activity;
  if (load === null || intensity === null || thresholdUsed === null) return null;
  const hours = (activity.movingS / 3600).toFixed(2);
  return {
    math: `${hours} H  ×  ${intensity.toFixed(2)}²  ×  100  =  ${Math.round(load)}`,
    threshold: thresholdText(activity, thresholdUsed),
  };
}

export function summaryTiles(a: ActivityDetail): Tile[] {
  return [
    { key: 'duration', label: 'DURATION', value: formatDuration(a.durationS), note: `MOVING ${formatDuration(a.movingS)}` },
    { key: 'distance', label: 'DISTANCE', value: orDash(a.distanceM, formatKmValue), note: 'KM' },
    { key: 'elevation', label: 'ELEVATION', value: orDash(a.elevationM, formatWhole), note: 'M GAINED' },
    { key: 'wpower', label: 'W. POWER', value: orDash(a.weightedPower, formatWhole), note: a.avgPower === null ? 'W' : `W · AVG ${Math.round(a.avgPower)} W` },
    { key: 'intensity', label: 'INTENSITY', value: orDash(a.intensity, (i) => i.toFixed(2)), note: 'OF FTP' },
    { key: 'hr', label: 'HEART RATE', value: orDash(a.avgHr, formatWhole), note: a.maxHr === null ? 'BPM AVG' : `BPM AVG · MAX ${Math.round(a.maxHr)}` },
  ];
}

type EyebrowFields = Pick<ActivitySummary, 'sport' | 'source' | 'localDate' | 'startTime' | 'sourceSport'>;

// Non-rides name Strava's sub-type and say whether there was GPS, once the streams have told us.
export function detailEyebrow(a: EyebrowFields, gps: boolean | null = null): string {
  const via = a.source === 'fit' ? '.FIT' : 'STRAVA';
  const named = a.sport === 'ride' || a.sport === 'run' ? null : sourceSportLabel(a.sourceSport);
  const subType = named === null ? '' : ` (${named})`;
  const gpsPart = a.sport === 'ride' || gps === null ? '' : gps ? ' · GPS' : ' · NO GPS';
  return `${a.sport.toUpperCase()} · ${formatWeekdayMonthDay(a.localDate)} · ${formatTimeOfDay(a.startTime)} · VIA ${via}${subType}${gpsPart}`;
}

export function routeLabel(a: ActivityDetail): string | null {
  if (a.distanceM === null) return null;
  const gained = a.elevationM === null ? '' : ` · ${Math.round(a.elevationM)} M GAINED`;
  return `ROUTE · ${formatKm(a.distanceM)}${gained}`;
}
